/**
 * ============================================================================
 * USER PAGE: Create / Edit Tiny URL Page
 * ============================================================================
 * Purpose:
 *   Dedicated page for creating and modifying dynamic redirection links.
 * ============================================================================
 */

import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import Swal from "sweetalert2";
import {
  ArrowLeftIcon,
  LinkIcon,
  GlobeAltIcon,
  ClockIcon,
  CheckIcon,
  SparklesIcon,
} from "@heroicons/react/24/outline";

import tinyUrlService from "../../services/tinyUrlService";
import { getCurrentUser, hasPermission } from "../../../core/utils/auth";

export default function CreateTinyUrl() {
  const { id } = useParams();
  const navigate = useNavigate();
  const currentUser = getCurrentUser();
  const isEditing = Boolean(id);

  const isPermitted = hasPermission(currentUser, "TINY_URL_MANAGEMENT");

  const [formData, setFormData] = useState({
    title: "",
    originalDestinationUrl: "",
    description: "",
    expirationPreset: "never",
    customDate: "",
    customCode: "",
  });

  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const frontendBase = window.location.origin;

  // Load existing link if editing
  useEffect(() => {
    if (!id || !isPermitted) return;
    setLoading(true);
    tinyUrlService
      .getById(id)
      .then((res) => {
        if (res && res.success && res.data) {
          const l = res.data;
          let preset = "never";
          let dateStr = "";
          if (l.expirationDate) {
            preset = "custom";
            dateStr = new Date(l.expirationDate).toISOString().slice(0, 16);
          }
          setFormData({
            title: l.title || "",
            originalDestinationUrl: l.originalDestinationUrl || "",
            description: l.description || "",
            expirationPreset: preset,
            customDate: dateStr,
            customCode: l.code || "",
          });
        }
      })
      .catch((err) => {
        Swal.fire({
          title: "Link Not Found",
          text: err.response?.data?.error || err.message,
          icon: "error",
        });
        navigate("/workspace/tiny-urls");
      })
      .finally(() => setLoading(false));
  }, [id, isPermitted, navigate]);

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.originalDestinationUrl.trim()) {
      Swal.fire({
        title: "Destination URL Required",
        text: "Please provide a valid destination URL to redirect visitors to.",
        icon: "warning",
        confirmButtonColor: "#3a57e8",
      });
      return;
    }

    setSubmitting(true);
    try {
      const payload = {
        title: formData.title.trim() || "Untitled Link",
        description: formData.description.trim(),
        originalDestinationUrl: formData.originalDestinationUrl.trim(),
        expirationPreset: formData.expirationPreset,
        expirationDate: formData.expirationPreset === "custom" ? formData.customDate : null,
      };

      if (!isEditing && formData.customCode.trim()) {
        payload.customCode = formData.customCode.trim();
      }

      if (isEditing) {
        await tinyUrlService.update(id, payload);
        await Swal.fire({
          title: "Tiny URL Updated!",
          text: `Link "${formData.title || formData.customCode}" has been updated.`,
          icon: "success",
          timer: 1800,
          showConfirmButton: false,
        });
      } else {
        const res = await tinyUrlService.create(payload);
        const code = res.data?.code;
        const shortUrl = `${frontendBase}/${code}`;
        await Swal.fire({
          title: "Tiny URL Created!",
          html: `<div class="text-sm">Your new short link is ready:<br/><b class="text-[#3a57e8] font-mono text-base">${shortUrl}</b></div>`,
          icon: "success",
          confirmButtonColor: "#3a57e8",
        });
      }

      navigate("/workspace/tiny-urls");
    } catch (err) {
      Swal.fire({
        title: isEditing ? "Update Failed" : "Creation Failed",
        text: err.response?.data?.error || err.message || "An unexpected error occurred",
        icon: "error",
        confirmButtonColor: "#ef4444",
      });
    } finally {
      setSubmitting(false);
    }
  };

  if (!isPermitted) {
    return (
      <div className="p-8 max-w-lg mx-auto text-center mt-12 bg-white dark:bg-gray-800 rounded-3xl shadow-xl">
        <h2 className="text-xl font-bold text-gray-900 dark:text-white">403 - Forbidden</h2>
        <p className="mt-2 text-sm text-gray-500">
          You do not have permission to manage Tiny URLs.
        </p>
        <button
          onClick={() => navigate("/workspace/tiny-urls")}
          className="mt-6 px-5 py-2.5 rounded-xl bg-[#3a57e8] text-white text-xs font-semibold cursor-pointer"
        >
          Return to Dashboard
        </button>
      </div>
    );
  }

  const previewCode = formData.customCode.trim() || "xYz123";
  const previewShortUrl = `${frontendBase}/${previewCode}`;

  return (
    <div className="p-4 sm:p-6 lg:p-8 space-y-6 max-w-4xl mx-auto">
      {/* Top Header */}
      <div className="flex items-center justify-between gap-4">
        <div className="flex items-center gap-3">
          <button
            onClick={() => navigate("/workspace/tiny-urls")}
            className="inline-flex items-center gap-2 px-3 py-2 rounded-xl border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 text-xs font-semibold shadow-xs cursor-pointer transition-colors"
          >
            <ArrowLeftIcon className="w-4 h-4" />
            Back to Tiny URLs
          </button>
          <div className="h-4 w-px bg-gray-300 dark:bg-gray-700" />
          <h1 className="text-lg sm:text-xl font-bold text-gray-900 dark:text-white">
            {isEditing ? "Edit Tiny URL" : "Create New Tiny URL"}
          </h1>
        </div>
      </div>

      {loading ? (
        <div className="bg-white dark:bg-gray-800 rounded-2xl p-12 text-center text-gray-400">
          Loading link data...
        </div>
      ) : (
        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Main Form Card */}
          <div className="bg-white dark:bg-gray-800 rounded-2xl p-6 sm:p-8 border border-gray-200/80 dark:border-gray-700/80 shadow-xs space-y-5 text-xs">
            {/* Title */}
            <div>
              <label className="block font-bold text-gray-700 dark:text-gray-200 mb-1.5 text-xs">
                Link Title <span className="text-gray-400 font-normal">(Internal Dashboard Reference)</span>
              </label>
              <input
                type="text"
                placeholder="e.g. WhatsApp Customer Support / Product Launch"
                value={formData.title}
                onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                className="w-full px-4 py-2.5 rounded-xl border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-[#3a57e8] transition-all"
              />
            </div>

            {/* Destination URL */}
            <div>
              <label className="block font-bold text-gray-700 dark:text-gray-200 mb-1.5 text-xs">
                Original Destination URL <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                placeholder="https://api.whatsapp.com/send?phone= or https://example.com/promo"
                value={formData.originalDestinationUrl}
                onChange={(e) => setFormData({ ...formData, originalDestinationUrl: e.target.value })}
                className="w-full px-4 py-2.5 rounded-xl border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-[#3a57e8] transition-all font-mono"
                required
              />
              <div className="mt-2 p-3 bg-blue-50/60 dark:bg-blue-950/30 rounded-xl border border-blue-100 dark:border-blue-900/40 text-[11px] text-gray-600 dark:text-gray-300 space-y-1">
                <span className="font-bold text-[#3a57e8] flex items-center gap-1">
                  <SparklesIcon className="w-3.5 h-3.5" />
                  Smart Dynamic Path Forwarding Supported:
                </span>
                <p>
                  If you end your URL with <code>?phone=</code>, visiting <code>{previewShortUrl}/919876543210</code> will automatically construct <code>https://api.whatsapp.com/send?phone=919876543210</code>.
                </p>
                <p>
                  Query parameters like <code>?src=wa&ref=partner</code> are always preserved and merged.
                </p>
              </div>
            </div>

            {/* Custom 6-Char Code (Only on Create) */}
            {!isEditing ? (
              <div>
                <label className="block font-bold text-gray-700 dark:text-gray-200 mb-1.5 text-xs">
                  Custom 6-Character Alphanumeric Code <span className="text-gray-400 font-normal">(Optional)</span>
                </label>
                <div className="flex items-center gap-2">
                  <span className="px-3.5 py-2.5 rounded-xl bg-gray-100 dark:bg-gray-700 border border-gray-300 dark:border-gray-600 font-mono text-gray-500">
                    {frontendBase}/
                  </span>
                  <input
                    type="text"
                    maxLength={6}
                    placeholder="e.g. C0mPhU"
                    value={formData.customCode}
                    onChange={(e) => setFormData({ ...formData, customCode: e.target.value.replace(/[^a-zA-Z0-9]/g, "") })}
                    className="w-40 px-4 py-2.5 rounded-xl border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white font-mono uppercase font-bold focus:outline-none focus:ring-2 focus:ring-[#3a57e8]"
                  />
                  <span className="text-gray-400 text-[11px]">
                    (Leave empty to generate random 6-character code automatically)
                  </span>
                </div>
              </div>
            ) : (
              <div>
                <label className="block font-bold text-gray-700 dark:text-gray-200 mb-1 text-xs">
                  Short Code (Strictly Immutable)
                </label>
                <span className="font-mono text-base font-bold text-[#3a57e8] bg-blue-50 dark:bg-blue-900/30 px-3 py-1.5 rounded-xl border border-blue-200/50 inline-block">
                  {formData.customCode}
                </span>
              </div>
            )}

            {/* Expiration Schedule */}
            <div>
              <label className="block font-bold text-gray-700 dark:text-gray-200 mb-2 text-xs">
                Expiration Schedule
              </label>
              <div className="grid grid-cols-2 sm:grid-cols-5 gap-2.5">
                {[
                  { id: "never", label: "Never" },
                  { id: "24h", label: "24 Hours" },
                  { id: "7d", label: "7 Days" },
                  { id: "30d", label: "30 Days" },
                  { id: "custom", label: "Custom Date" },
                ].map((preset) => (
                  <button
                    type="button"
                    key={preset.id}
                    onClick={() => setFormData({ ...formData, expirationPreset: preset.id })}
                    className={`py-2.5 px-3 rounded-xl border text-center font-semibold transition-all cursor-pointer ${
                      formData.expirationPreset === preset.id
                        ? "bg-[#3a57e8] border-[#3a57e8] text-white shadow-xs"
                        : "border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-600"
                    }`}
                  >
                    {preset.label}
                  </button>
                ))}
              </div>

              {formData.expirationPreset === "custom" && (
                <div className="mt-3">
                  <label className="block font-bold text-gray-700 dark:text-gray-200 mb-1 text-xs">
                    Choose Expiration Date & Time
                  </label>
                  <input
                    type="datetime-local"
                    value={formData.customDate}
                    onChange={(e) => setFormData({ ...formData, customDate: e.target.value })}
                    className="w-full sm:w-80 px-4 py-2.5 rounded-xl border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-[#3a57e8]"
                    required={formData.expirationPreset === "custom"}
                  />
                </div>
              )}
            </div>

            {/* Description Notes */}
            <div>
              <label className="block font-bold text-gray-700 dark:text-gray-200 mb-1.5 text-xs">
                Description / Notes <span className="text-gray-400 font-normal">(Optional)</span>
              </label>
              <textarea
                rows={3}
                placeholder="Campaign notes, WhatsApp broadcast target, or owner remarks..."
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                className="w-full px-4 py-2.5 rounded-xl border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-[#3a57e8]"
              />
            </div>
          </div>

          {/* Form Actions */}
          <div className="flex items-center justify-end gap-3">
            <button
              type="button"
              onClick={() => navigate("/workspace/tiny-urls")}
              className="px-5 py-2.5 rounded-xl border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-750 font-semibold cursor-pointer text-xs"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={submitting}
              className="px-6 py-2.5 rounded-xl bg-[#3a57e8] hover:bg-[#2e47d4] text-white font-semibold shadow-md shadow-[#3a57e8]/30 cursor-pointer disabled:opacity-50 text-xs transition-all"
            >
              {submitting ? "Saving Link..." : isEditing ? "Save Changes" : "Create Tiny URL"}
            </button>
          </div>
        </form>
      )}
    </div>
  );
}
